import { useQuery, useQueryClient } from '@tanstack/react-query';
import { STALE_TIME } from 'infrastructure/consts/stale-time';

import useApi from '../use-api';

import type { ModuleType } from 'infrastructure/enums';
import type {
  IPatientKeyNote,
  IUpdatePatientKeyNote,
} from 'infrastructure/interfaces';

const apiUrl = (patientGuid: string) => `core/patients/${patientGuid}/key-note`;

interface IValue {
  loading: boolean;
  data: IPatientKeyNote;
  update: (body: IUpdatePatientKeyNote) => Promise<void>;
}

interface IProps {
  patientGuid: string;
  module: ModuleType;
}

export const usePatientKeyNotes = (props: IProps): IValue => {
  const { patientGuid, module } = props;

  const { loading, loadData, updateData } = useApi();

  const queryClient = useQueryClient();
  const queryKey = ['usePatientKeyNotes', patientGuid, module];

  const { data = { note: '' }, isLoading } = useQuery({
    queryKey,
    queryFn: async () =>
      loadData<IPatientKeyNote>(apiUrl(patientGuid), {
        module,
      }),
    enabled: Boolean(patientGuid && module),
    staleTime: STALE_TIME,
  });

  const update = async (body: IUpdatePatientKeyNote): Promise<void> => {
    await updateData(apiUrl(patientGuid), body).then(() => {
      queryClient.setQueryData(queryKey, body);
    });
  };

  return {
    loading: loading || isLoading,
    data,
    update,
  };
};
